import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MainLayout from './layouts/MainLayout';
import MSMEDashboard from './pages/MSMEDashboard';
import InvestorPortal from './pages/InvestorPortal';
import ShariahCompliance from './pages/ShariahCompliance';
import ShariahReviewPage from './pages/ShariahReviewPage';
import TrusteeManagement from './pages/TrusteeManagement';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import DigitalAdvisorPortal from './pages/DigitalAdvisorPortal';
import RatingAgencyPortal from './pages/RatingAgencyPortal';
import RatingAssessment from './pages/RatingAssessment';
import MSMEApplications from './pages/MSMEApplications';
// Demo pages
import DemoLayout from './pages/demo/DemoLayout';
import RollingPayDashboard from './pages/demo/RollingPayDashboard';
import SukukInitiation from './pages/demo/SukukInitiation';
import DigitalAdvisorView from './pages/demo/DigitalAdvisorView';
import ListingProcess from './pages/demo/ListingProcess';
import InvestorView from './pages/demo/InvestorView';
import MicroSukukListing from './pages/demo/MicroSukukListing';
import MicroSukukDetails from './pages/demo/MicroSukukDetails';
// Protected route wrapper
const ProtectedRoute = ({
  children,
  allowedRoles
}: {
  children: JSX.Element;
  allowedRoles?: string[];
}) => {
  const {
    isAuthenticated,
    user
  } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace data-id="element-0" />;
  }
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to the user's default dashboard based on their role
    switch (user.role) {
      case 'msme':
        return <Navigate to="/msme-dashboard" replace data-id="element-1" />;
      case 'investor':
        return <Navigate to="/investor-portal" replace data-id="element-2" />;
      case 'shariah':
        return <Navigate to="/shariah-compliance" replace data-id="element-3" />;
      case 'trustee':
        return <Navigate to="/trustee-management" replace data-id="element-4" />;
      case 'digital-advisor':
        return <Navigate to="/digital-advisor-portal" replace data-id="element-5" />;
      case 'rating-agency':
        return <Navigate to="/rating-agency-portal" replace data-id="element-6" />;
      default:
        return <Navigate to="/" replace data-id="element-7" />;
    }
  }
  return children;
};
const AppRoutes = () => {
  const {
    isAuthenticated
  } = useAuth();
  return <Routes data-id="element-8">
      {/* Make login the default landing page */}
      <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace data-id="element-10" /> : <LoginPage data-id="element-11" />} data-id="element-9" />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace data-id="element-13" /> : <LoginPage data-id="element-14" />} data-id="element-12" />
      {/* Protected routes under MainLayout */}
      <Route path="/dashboard" element={<ProtectedRoute data-id="element-16">
            <MainLayout data-id="element-17" />
          </ProtectedRoute>} data-id="element-15">
        <Route index element={<LandingPage data-id="element-19" />} data-id="element-18" />
        <Route path="msme" element={<ProtectedRoute allowedRoles={['msme']} data-id="element-21">
              <MSMEDashboard data-id="element-22" />
            </ProtectedRoute>} data-id="element-20" />
        <Route path="msme-applications" element={<ProtectedRoute allowedRoles={['msme']} data-id="element-24">
              <MSMEApplications data-id="element-25" />
            </ProtectedRoute>} data-id="element-23" />
        <Route path="investor" element={<ProtectedRoute allowedRoles={['investor']} data-id="element-27">
              <InvestorPortal data-id="element-28" />
            </ProtectedRoute>} data-id="element-26" />
        <Route path="shariah" element={<ProtectedRoute allowedRoles={['shariah']} data-id="element-30">
              <ShariahCompliance data-id="element-31" />
            </ProtectedRoute>} data-id="element-29" />
        <Route path="shariah-review/:id" element={<ProtectedRoute allowedRoles={['shariah']} data-id="element-33">
              <ShariahReviewPage data-id="element-34" />
            </ProtectedRoute>} data-id="element-32" />
        <Route path="trustee" element={<ProtectedRoute allowedRoles={['trustee']} data-id="element-36">
              <TrusteeManagement data-id="element-37" />
            </ProtectedRoute>} data-id="element-35" />
        <Route path="digital-advisor" element={<ProtectedRoute allowedRoles={['digital-advisor', 'admin']} data-id="element-39">
              <DigitalAdvisorPortal data-id="element-40" />
            </ProtectedRoute>} data-id="element-38" />
        <Route path="rating-agency" element={<ProtectedRoute allowedRoles={['rating-agency', 'admin']} data-id="element-42">
              <RatingAgencyPortal data-id="element-43" />
            </ProtectedRoute>} data-id="element-41" />
        <Route path="rating-assessment" element={<ProtectedRoute allowedRoles={['rating-agency', 'admin']} data-id="element-45">
              <RatingAssessment data-id="element-46" />
            </ProtectedRoute>} data-id="element-44" />
      </Route>
      {/* Demo Routes */}
      <Route path="/demo" element={<DemoLayout data-id="element-48" />} data-id="element-47">
        <Route index element={<RollingPayDashboard data-id="element-50" />} data-id="element-49" />
        <Route path="sukuk-initiation" element={<SukukInitiation data-id="element-52" />} data-id="element-51" />
        <Route path="advisor-assessment" element={<DigitalAdvisorView data-id="element-54" />} data-id="element-53" />
        <Route path="listing-process" element={<ListingProcess data-id="element-56" />} data-id="element-55" />
        <Route path="investor-view" element={<InvestorView data-id="element-58" />} data-id="element-57" />
        <Route path="micro-sukuk-listing" element={<MicroSukukListing data-id="element-60" />} data-id="element-59" />
        <Route path="micro-sukuk-details" element={<MicroSukukDetails data-id="element-62" />} data-id="element-61" />
      </Route>
    </Routes>;
};
export function App() {
  return <Router data-id="element-63">
      <AuthProvider data-id="element-64">
        <AppRoutes data-id="element-65" />
      </AuthProvider>
    </Router>;
}